import { z } from "zod";
import { STANDARD_EVENTS } from "./events";

/**
 * Who sees a goal's Meta ads (notes/AUDIENCE.md section 3): places, ages, starting points, the product's
 * client lists, and how far Meta may look. It lives at GoalConfig.channels.meta.settings.audience; every
 * place and starting point carries the id Meta's own targeting search returned, never an invented one.
 */

export const PLACE_KINDS = ["country", "region", "city", "zip", "metro", "pin"] as const;
/** Meta's limits on a radius, in kilometres (Zernio docs: Meta's city minimum is about 17 km). */
export const CITY_RADIUS_KM = { min: 17, max: 80 } as const;
export const PIN_RADIUS_KM = { min: 1, max: 80 } as const;

export const PlaceSchema = z
  .object({
    kind: z.enum(PLACE_KINDS),
    /** Meta's key from targeting search (a country's is its ISO code); absent only for a pin. */
    key: z.string().min(1).optional(),
    name: z.string().trim().min(1).max(200),
    country: z.string().length(2),
    radiusKm: z.number().positive().max(80).optional(),
    lat: z.number().min(-90).max(90).optional(),
    lng: z.number().min(-180).max(180).optional(),
  })
  .superRefine((p, ctx) => {
    if (p.kind === "pin") {
      if (p.lat === undefined || p.lng === undefined || p.radiusKm === undefined) ctx.addIssue({ code: "custom", message: "A pin needs its point and a distance." });
    } else if (!p.key) ctx.addIssue({ code: "custom", message: `${p.name} has no id from Meta's list.` });
  });
export type Place = z.infer<typeof PlaceSchema>;

export const START_KINDS = ["interest", "behavior", "lifeEvent", "relationship", "income", "workPosition", "workEmployer", "workIndustry"] as const;
export type StartKind = (typeof START_KINDS)[number];

export const StartSchema = z.object({
  kind: z.enum(START_KINDS),
  /** Meta's id exactly as targeting search returned it (an income tier is its tier name). */
  id: z.string().min(1),
  name: z.string().trim().min(1).max(200),
  size: z.object({ lower: z.number(), upper: z.number() }).nullable().default(null),
});
export type Start = z.infer<typeof StartSchema>;

export const AUDIENCE_PARTS = ["places", "ages", "starts", "lists", "reach"] as const;

const EventNameSchema = z.string().trim().min(1).max(60);
const CustomAudienceSchema = z.object({ id: z.string().regex(/^\d{5,25}$/, "A Meta audience id is digits."), name: z.string().trim().min(1).max(200) });

const AudienceObject = z.object({
    /** What the sentence calls these people, one plural word or two ("couples", "agents"). */
    noun: z.string().trim().min(1).max(40).optional(),
    places: z.object({
      include: z.array(PlaceSchema).default([]),
      exclude: z.array(PlaceSchema).default([]),
    }),
    ages: z.object({ min: z.number().int().min(18).max(65), max: z.number().int().min(18).max(65) }).default({ min: 18, max: 65 }),
    /** No control on screen; set only when the owner asks the agent. */
    gender: z.enum(["all", "female", "male"]).default("all"),
    /** Meta's language codes ("en", "hi"); no control on screen, same rule. */
    languages: z.array(z.string().min(2).max(10)).default([]),
    starts: z.array(StartSchema).default([]),
    /** The product's client lists this goal uses. None until the owner ticks one. */
    lists: z
      .array(
        z.object({
          id: z.string().min(1),
          /** Show the ads to the people on the list themselves. */
          start: z.boolean().default(false),
          like: z.boolean(),
          likeRatio: z.number().min(0.01).max(0.1).default(0.01),
          out: z.boolean(),
        }),
      )
      .default([]),
    /** The product pixel's events, by Meta's name: the people who sent one in the last 180 days, as a start or left out. */
    events: z
      .object({ start: z.array(EventNameSchema).max(10).default([]), out: z.array(EventNameSchema).max(10).default([]) })
      .default({ start: [], out: [] })
      .describe("The pixel's events, by the exact names list_events gives: start, Meta starts with the people who sent one; out, they never see the ads"),
    /** Audiences the owner made in their Meta ad account, by Meta's id: the people in one, as a start or left out. */
    custom: z
      .object({ start: z.array(CustomAudienceSchema).max(20).default([]), out: z.array(CustomAudienceSchema).max(20).default([]) })
      .default({ start: [], out: [] })
      .describe("Audiences from the owner's own Meta ad account; the owner picks them, so leave both empty"),
    /** "start": Meta may find more people like these; "fence": only the people chosen. */
    reach: z.enum(["start", "fence"]).default("start"),
    why: z.partialRecord(z.enum(AUDIENCE_PARTS), z.string().max(600)).default({}),
});

const audienceRules = (a: z.infer<typeof AudienceObject>, ctx: z.RefinementCtx) => {
  if (a.ages.min > a.ages.max) ctx.addIssue({ code: "custom", message: "The youngest age is above the oldest." });
  if (a.starts.filter((s) => s.kind === "income").length > 1) ctx.addIssue({ code: "custom", message: "Pick one household income band." });
  for (const e of a.events.start) if (a.events.out.includes(e)) ctx.addIssue({ code: "custom", message: `${e} is both a start and left out. Keep one.` });
  for (const c of a.custom.start) if (a.custom.out.some((x) => x.id === c.id)) ctx.addIssue({ code: "custom", message: `${c.name} is both a start and left out. Keep one.` });
  if (a.lists.some((l) => l.start && l.out)) ctx.addIssue({ code: "custom", message: "A client list cannot be both a start and left out. Keep one." });
};

export const AudienceSchema = AudienceObject.superRefine(audienceRules);
export type Audience = z.infer<typeof AudienceSchema>;

/** The audience the plan proposes: the same shape, plus where its size came from. */
export const PlanAudienceSchema = AudienceObject.extend({
  size_basis: z.string().describe("Where the size came from: Meta's reach estimate, with its numbers"),
}).superRefine(audienceRules);

/** Meta's own words for the relationship statuses it documents (Marketing API, basic targeting). */
export const RELATIONSHIP_STATUSES = [
  { id: "1", name: "Single" },
  { id: "2", name: "In a relationship" },
  { id: "3", name: "Married" },
  { id: "4", name: "Engaged" },
] as const;

// --- Rules on places ----------------------------------------------------------------------

/** What is wrong with one place, in the words the screen shows, or null. */
export function placeProblem(p: Place): string | null {
  if (p.kind === "city" && p.radiusKm !== undefined && p.radiusKm < CITY_RADIUS_KM.min) return `Meta needs at least ${CITY_RADIUS_KM.min} km around a city. Use a pin for a smaller area.`;
  if (p.kind === "pin" && (p.radiusKm === undefined || p.radiusKm < PIN_RADIUS_KM.min || p.radiusKm > PIN_RADIUS_KM.max)) return `A pin needs a distance from ${PIN_RADIUS_KM.min} to ${PIN_RADIUS_KM.max} km.`;
  return null;
}

const samePlace = (a: Place, b: Place) => a.kind === b.kind && (a.kind === "pin" ? a.lat === b.lat && a.lng === b.lng : a.key === b.key);

/**
 * Add a place to a list. Meta refuses a city with the country it sits in, so adding one replaces the
 * other: a country takes the place of the smaller places inside it, and a smaller place takes the place
 * of its country. Returns the list and what it replaced, for the screen to say so.
 */
export function withPlace(places: Place[], p: Place): { places: Place[]; replaced: Place[] } {
  if (places.some((x) => samePlace(x, p))) return { places, replaced: [] };
  const replaced = places.filter((x) => (p.kind === "country" ? x.kind !== "country" && x.country === p.country : x.kind === "country" && x.country === p.country));
  return { places: [...places.filter((x) => !replaced.includes(x)), p], replaced };
}

/** What still stops the audience from going to Meta, in the order the screen lists them. */
export function audienceProblems(a: Audience | undefined | null, readyLists: (id: string) => boolean = () => true): string[] {
  if (!a) return ["Add at least one place."];
  const out: string[] = [];
  if (!a.places.include.length) out.push("Add at least one place.");
  for (const p of [...a.places.include, ...a.places.exclude]) {
    const why = placeProblem(p);
    if (why) out.push(`${p.name}: ${why}`);
  }
  const countries = new Set(a.places.include.filter((p) => p.kind === "country").map((p) => p.country));
  for (const p of a.places.include) if (p.kind !== "country" && countries.has(p.country)) out.push(`${p.name} sits inside a country already chosen. Keep one of them.`);
  if (a.starts.some((s) => s.kind === "income") && !incomeAllowed(a)) out.push(INCOME_US_ONLY);
  for (const l of a.lists) if ((l.start || l.like || l.out) && !readyLists(l.id)) out.push("A client list this goal uses is not ready yet.");
  return out;
}

/** Meta's household income bands are by United States ZIP code only (live check, 2 Oct 2026). */
export const INCOME_US_ONLY = "Meta's household income bands cover the United States only. Remove the income band, or show the ads only in the United States.";
export const incomeAllowed = (a: Pick<Audience, "places">) => a.places.include.length > 0 && a.places.include.every((p) => p.country === "US");

// --- What Meta gets -----------------------------------------------------------------------

/** The youngest age Meta keeps while it may look wider (Meta: 18 to 25 under Advantage+ audience). */
export const KEPT_AGE_CAP = 25;

export interface MetaTargeting {
  /** The flat camelCase fields of Zernio's create body (and of a reach estimate's spec). */
  flat: Record<string, unknown>;
  /** Zernio's nested `targeting` object: excluded places and the client lists. */
  nested: Record<string, unknown>;
  /** Meta's own snake_case targeting, sent as rawTargeting: what Zernio's camelCase does not carry. */
  raw: Record<string, unknown>;
}

const km = (radius: number) => ({ radius, distanceUnit: "kilometer" as const });

function geo(places: Place[]) {
  const by = (k: Place["kind"]) => places.filter((p) => p.kind === k);
  const out: Record<string, unknown> = {};
  const countries = by("country").map((p) => p.key ?? p.country);
  if (countries.length) out.countries = countries;
  if (by("region").length) out.regions = by("region").map((p) => ({ key: p.key, name: p.name }));
  if (by("city").length) out.cities = by("city").map((p) => ({ key: p.key, name: p.name, ...(p.radiusKm ? km(p.radiusKm) : {}) }));
  if (by("zip").length) out.zips = by("zip").map((p) => ({ key: p.key, name: p.name }));
  if (by("metro").length) out.metros = by("metro").map((p) => ({ key: p.key, name: p.name }));
  if (by("pin").length) out.customLocations = by("pin").map((p) => ({ latitude: p.lat, longitude: p.lng, name: p.name, ...km(p.radiusKm ?? PIN_RADIUS_KM.min) }));
  return out;
}

/**
 * The audience as Meta targeting, through Zernio (notes/AUDIENCE.md 7.1). `lists` are the Meta ids of the
 * product's ready audiences: the lookalikes and the pixel's events to start from, and the lists and events to leave out.
 */
export function toMetaTargeting(a: Audience, lists: { include: string[]; exclude: string[] } = { include: [], exclude: [] }): MetaTargeting {
  const flat: Record<string, unknown> = { ...geo(a.places.include) };
  const start = a.reach === "start";
  // Why: under Advantage+ audience Meta keeps a youngest age only up to 25 and treats the range as a suggestion.
  const kept = start ? Math.min(a.ages.min, KEPT_AGE_CAP) : a.ages.min;
  flat.ageMin = kept;
  flat.ageMax = start ? 65 : a.ages.max;
  if (a.gender !== "all") flat.gender = a.gender;
  if (a.languages.length) flat.languages = a.languages;
  const ids = (k: StartKind) => a.starts.filter((s) => s.kind === k).map((s) => ({ id: s.id, name: s.name }));
  for (const [kind, field] of [
    ["interest", "interests"],
    ["behavior", "behaviors"],
    ["workPosition", "workPositions"],
    ["workEmployer", "workEmployers"],
    ["workIndustry", "workIndustries"],
  ] as const) {
    const v = ids(kind);
    if (v.length) flat[field] = v;
  }
  const income = a.starts.find((s) => s.kind === "income");
  if (income) flat.incomeTier = income.id;

  const nested: Record<string, unknown> = {};
  if (a.places.exclude.length) nested.excludedLocations = geo(a.places.exclude);
  if (lists.include.length) nested.audienceInclude = lists.include;
  if (lists.exclude.length) nested.audienceExclude = lists.exclude;

  // Why: the switch and "places stay fixed" travel together in rawTargeting; a flat advantageAudience would replace the whole object.
  const raw: Record<string, unknown> = { targeting_automation: { advantage_audience: start ? 1 : 0, individual_setting: { geo: 0 } } };
  if (start && (a.ages.min > kept || a.ages.max < 65)) raw.age_range = [a.ages.min, a.ages.max];
  const life = ids("lifeEvent");
  if (life.length) raw.life_events = life;
  const statuses = a.starts.filter((s) => s.kind === "relationship").map((s) => Number(s.id));
  if (statuses.length) raw.relationship_statuses = statuses;
  return { flat, nested, raw };
}

/**
 * The flat targeting as the create body takes it (docs: POST /v1/ads/create `cities` items carry
 * `distance_unit`, while the targeting spec of reach estimates and nested targeting carries `distanceUnit`;
 * Meta's answer through Zernio confirmed the create body's, 2 Oct 2026).
 */
export function forCreate(flat: Record<string, unknown>): Record<string, unknown> {
  const cities = flat.cities as { radius?: number; distanceUnit?: string }[] | undefined;
  if (!cities) return flat;
  return { ...flat, cities: cities.map(({ distanceUnit, ...c }) => (distanceUnit ? { ...c, distance_unit: distanceUnit } : c)) };
}

/**
 * The audience as a change to a running ad set (Zernio's PUT /v1/ads/{adId} `targeting`, notes/AUDIENCE.md
 * 7.3). It is a partial update, so every part Flashadz manages is sent, empty ones cleared, and all the
 * places together (any location field replaces the whole set). It takes no raw Meta fields: the age
 * suggestion under Advantage+ and life events or relationship statuses stay as they were (RIGID 105).
 */
export function toMetaUpdate(a: Audience, lists: { include: string[]; exclude: string[] } = { include: [], exclude: [] }): Record<string, unknown> {
  const t = toMetaTargeting(a, lists);
  const cleared = {
    gender: "all",
    interests: [],
    behaviors: [],
    workPositions: [],
    workEmployers: [],
    workIndustries: [],
    incomeTier: null,
    excludedLocations: null,
    audienceInclude: [],
    audienceExclude: [],
  };
  return { ...cleared, ...t.flat, ...t.nested, advantage_audience: a.reach === "start" ? 1 : 0 };
}

/** A targeting spec for Zernio's reach estimate: the camelCase part only (it takes no raw Meta fields). */
export function reachSpec(a: Audience, lists?: { include: string[]; exclude: string[] }): Record<string, unknown> {
  const t = toMetaTargeting(a, lists);
  return { ...t.flat, ...t.nested };
}

// --- The size band (notes/AUDIENCE.md 10.3) ------------------------------------------------

/** A first rule, tuned against real goals (RIGID): Meta's cost per 1,000 views, and how often one person sees the ads in a week. */
export const SIZE_BAND = { tooFewTimes: 20, veryWideTimes: 2000, cpmCents: 1000, weeklyFrequency: 1.5 } as const;

/** Whether Meta's estimate fits the daily amount: too few people, right, or very wide. */
export function sizeBand(upper: number, dailyCents: number, cpmCents: number = SIZE_BAND.cpmCents): "few" | "right" | "wide" {
  const weekly = (dailyCents * 7 * 1000) / Math.max(1, cpmCents) / SIZE_BAND.weeklyFrequency;
  if (upper < weekly * SIZE_BAND.tooFewTimes) return "few";
  if (upper > weekly * SIZE_BAND.veryWideTimes) return "wide";
  return "right";
}

// --- The sentence (notes/AUDIENCE.md 5.1) --------------------------------------------------

/** One piece of the sentence: plain words, or a part the owner taps, kept or a start. */
export type SentencePiece = { text: string } | { text: string; part: (typeof AUDIENCE_PARTS)[number]; look: "kept" | "start" };

const THE = new Set(["US", "GB", "AE", "NL", "PH", "CZ", "BS", "GM", "MV", "DO", "CF", "CG", "CD", "KM", "SC", "SB"]);
const COUNTRY_NAMES: Record<string, string> = { US: "United States", GB: "United Kingdom", AE: "United Arab Emirates", NL: "Netherlands", PH: "Philippines", CZ: "Czech Republic" };

/** "Bengaluru and Mumbai", "the United States", "Austin (+25 km)". */
export function placeWords(p: Place): string {
  if (p.kind === "country") {
    const name = COUNTRY_NAMES[p.country] ?? p.name;
    return THE.has(p.country) && !/^the /i.test(name) ? `the ${name}` : name;
  }
  return p.radiusKm && p.kind !== "pin" ? `${p.name} (+${p.radiusKm} km)` : p.kind === "pin" ? `${p.radiusKm} km around ${p.name}` : p.name;
}

export const andList = (xs: string[]) => (xs.length <= 1 ? (xs[0] ?? "") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);
const orList = (xs: string[]) => (xs.length <= 1 ? (xs[0] ?? "") : `${xs.slice(0, -1).join(", ")} or ${xs[xs.length - 1]}`);
/** People the pixel saw do something, in plain words: "people who signed up, by your pixel". A site's own event keeps its name. */
export const pixelPeople = (events: string[]) => {
  const did = events.map((e) => (e === "PageView" ? "visited your page" : STANDARD_EVENTS[e]?.words.replace(/^Someone /, "")));
  return did.every(Boolean) ? `people who ${orList(did as string[])}, by your pixel` : `people your pixel saw with ${orList(events)}`;
};

const INCOME_WORDS: Record<string, string> = { top_5: "a top 5%", top_10: "a top 10%", top_10_25: "a top 10 to 25%", top_25_50: "a top 25 to 50%" };

/** How a starting point reads in the sentence. */
export function startWords(s: Start): string {
  if (s.kind === "relationship") return `people who are ${s.name.toLowerCase()}`;
  if (s.kind === "lifeEvent") return `the ${s.name.replace(/^Newly /, "newly ").replace(/\s*\(.*\)$/, "")}`;
  if (s.kind === "income") return `people with ${INCOME_WORDS[s.id] ?? s.name} household income`;
  if (s.kind === "workPosition") return plural(s.name);
  return s.name;
}

/** "Real estate agent" is "Real estate agents"; a title already plural stays. */
const plural = (title: string) => (/s$/i.test(title) ? title : /[^aeiou]y$/i.test(title) ? `${title.slice(0, -1)}ies` : `${title}s`);

/**
 * The audience in one sentence, every part the owner can change marked: places and the kept youngest age
 * are always kept; ages beyond it, starting points and people like the lists are where Meta starts.
 */
export function audienceSentence(a: Audience, o: { listNames?: (id: string) => string | undefined } = {}): SentencePiece[] {
  const out: SentencePiece[] = [{ text: `Show the ads to ${a.noun ?? "people"} in ` }];
  out.push({ text: andList(a.places.include.map(placeWords)) || "no place yet", part: "places", look: "kept" });
  if (a.places.exclude.length) out.push({ text: ", leaving out " }, { text: andList(a.places.exclude.map(placeWords)), part: "places", look: "kept" });
  const ages = a.ages.max >= 65 ? `${a.ages.min} and over` : `${a.ages.min} to ${a.ages.max}`;
  out.push({ text: ", aged " }, { text: ages, part: "ages", look: a.reach === "fence" ? "kept" : "start" });
  const like = a.lists.filter((l) => l.like).map((l) => `people like ${o.listNames?.(l.id) ?? "your past clients"}`);
  const people = a.starts.filter((s) => s.kind === "relationship" || s.kind === "lifeEvent" || s.kind === "income").map(startWords);
  const jobs = a.starts.filter((s) => s.kind === "workPosition").map(startWords);
  const into = a.starts.filter((s) => s.kind === "interest" || s.kind === "behavior" || s.kind === "workEmployer" || s.kind === "workIndustry").map((s) => s.name);
  const startParts: SentencePiece[] = [];
  const add = (text: string, part: "starts" | "lists") => startParts.push(...(startParts.length ? [{ text: ", " }] : []), { text, part, look: "start" as const });
  const own = [...a.lists.filter((l) => l.start).map((l) => o.listNames?.(l.id) ?? "your past clients"), ...a.custom.start.map((c) => c.name)];
  if (own.length) add(`the people in ${orList(own)}`, "lists");
  for (const l of like) add(l, "lists");
  if (a.events.start.length) add(pixelPeople(a.events.start), "lists");
  for (const p of people) add(p, "starts");
  // Why: a plan starts with every title Meta lists for a job, often a dozen; the Who block lists them all.
  if (jobs.length) add(jobs.length > 3 ? `${jobs.slice(0, 2).join(", ")} or ${jobs.length - 2} more job titles` : orList(jobs), "starts");
  if (into.length) add(`people into ${orList(into)}`, "starts");
  if (startParts.length) out.push({ text: ", starting with " }, ...startParts);
  const out2 = a.lists.filter((l) => l.out).map((l) => o.listNames?.(l.id) ?? "your past clients");
  if (a.events.out.length) out2.push(pixelPeople(a.events.out));
  out2.push(...a.custom.out.map((c) => c.name));
  if (out2.length) out.push({ text: ", leaving out " }, { text: andList(out2), part: "lists", look: "kept" });
  out.push({ text: "." });
  return out;
}
